import React, { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";
import { getApiEndpoint } from "../../../../utils/apiConfig";
import axiosWithAuth from "../../../../utils/axiosWithAuth";

const RepairTeamManager = () => {
  const [repairTeams, setRepairTeams] = useState({
    name: "",
    leader: "",
    repairmen: [],
  });
  const [repairTeamList, setRepairTeamList] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [repairmen, setRepairmen] = useState([]);
  const authToken = localStorage.getItem("authToken");
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchRepairTeam();
    fetchLeaders();
    fetchRepairmen();
  }, []);

  // Repair Team List
  const fetchRepairTeam = () => {
    axiosWithAuth()
      .get("repair-team")
      .then((response) => setRepairTeamList(response.data))
      .catch((error) => console.error("Failed to fetch repair teams:", error));
  };

  // Leader List
  const fetchLeaders = () => {
    axiosWithAuth()
      .get("repairman")
      .then((response) => setLeaders(response.data))
      .catch((error) => console.error("Failed to fetch leaders:", error));
  };

  // Repairmen List
  const fetchRepairmen = () => {
    axiosWithAuth()
      .get("repairman")
      .then((response) => setRepairmen(response.data))
      .catch((error) => console.error("Failed to fetch repairmen:", error));
  };

  // Form Submit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Create a properly formatted payload matching the API requirements
    const payload = {
      name: repairTeams.name,
      // Send just the ID string for leader, not an object
      leader: repairTeams.leader,
      // Send an array of repairmen IDs, not objects with id property
      repairmen: repairTeams.repairmen
    };

    console.log("Submitting payload:", payload);

    const url = isEditing
      ? `repair-team/${editId}`
      : "repair-team";

    const request = isEditing
      ? axiosWithAuth().put(url, payload)
      : axiosWithAuth().post(url, payload);

    request
      .then((response) => {
        console.log("Success response:", response.data);
        fetchRepairTeam();
        setRepairTeams({ name: "", leader: "", repairmen: [] });
        setIsEditing(false);
        setEditId(null);
        swal({
          title: "",
          text: isEditing ? "Repair team has been updated" : "Repair team has been added",
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.error("Failed to save repair team:", error);
        // Show more detailed error message
        const errorMessage = error.response?.data?.message || "Failed to save repair team";
        swal({
          title: "Error",
          text: errorMessage,
          icon: "error",
          timer: 3000,
        });
      });
  };

  // Edit Handler
  const handleEdit = (team) => {
    console.log("Editing team:", team);
    
    // Handle both id and _id formats
    const teamId = team.id || team._id;
    
    // Get the leader ID directly (not as an object)
    let leaderId = "";
    if (team.leader) {
      // Handle both formats of leader ID (string or object with id/._id property)
      if (typeof team.leader === 'string') {
        leaderId = team.leader;
      } else if (team.leader.id) {
        leaderId = team.leader.id;
      } else if (team.leader._id) {
        leaderId = team.leader._id;
      }
    }
    
    // Get the repairmen IDs as an array of strings
    const repairmenIds = team.repairmen 
      ? team.repairmen.map(rm => {
          if (typeof rm === 'string') return rm;
          return rm.id || rm._id;
        })
      : [];
    
    setRepairTeams({
      name: team.name,
      leader: leaderId,
      repairmen: repairmenIds,
    });
    
    setEditId(teamId);
    setIsEditing(true);
  };

  // Delete Handler
  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this repair team!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
    .then((willDelete) => {
      if (willDelete) {
        axiosWithAuth()
          .delete(`repair-team/${id}`)
          .then(() => {
            fetchRepairTeam();
            swal("Repair team has been deleted!", {
              icon: "success",
              timer: 1500
            });
          })
          .catch((error) => {
            console.error("Failed to delete repair team:", error);
            swal("Error deleting repair team!", {
              icon: "error",
              timer: 1500
            });
          });
      }
    });
  };

  // Checkbox Change
  const handleRepairmenChange = (id) => {
    setRepairTeams((prev) => {
      // Handle adding/removing repairmen IDs from the array
      if (prev.repairmen.includes(id)) {
        return { ...prev, repairmen: prev.repairmen.filter((r) => r !== id) };
      } else {
        return { ...prev, repairmen: [...prev.repairmen, id] };
      }
    });
  };

  // Render repairman list
  const renderRepairmanList = () => {
    if (repairmen.length === 0) {
      return (
        <div className="alert alert-info">
          No repairmen available. Please add repairmen first.
        </div>
      );
    }
    
    return (
      <div className="d-flex flex-wrap gap-2">
        {repairmen.map((repairman) => {
          const repairmanId = repairman.id || repairman._id;
          return (
            <div key={repairmanId} className="form-check">
              <input
                type="checkbox"
                value={repairmanId}
                checked={repairTeams.repairmen.includes(repairmanId)}
                onChange={() => handleRepairmenChange(repairmanId)}
                className="form-check-input"
              />
              <label className="form-check-label">
                {repairman.name} {repairman.surname}
              </label>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="repairTeams-manager">
      <h3 className="text-center">Repair Team Manager</h3>
      <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
        {/* Name Input */}
        <div>
          <label className="form-label">Name</label>
          <input
            type="text"
            value={repairTeams.name}
            onChange={(e) =>
              setRepairTeams({ ...repairTeams, name: e.target.value })
            }
            required
            className="form-control"
          />
        </div>

        {/* Leader Select */}
        <div>
          <label className="form-label">Leader</label>
          <select
            value={repairTeams.leader}
            onChange={(e) =>
              setRepairTeams({ ...repairTeams, leader: e.target.value })
            }
            className="form-control"
            required
          >
            <option key="select-leader" value="">Select Leader</option>
            {leaders.map((leader) => (
              <option key={leader.id || leader._id} value={leader.id || leader._id}>
                {leader.name} {leader.surname}
              </option>
            ))}
          </select>
        </div>

        {/* Repairmen Checkbox */}
        <div>
          <label className="form-label">Repairmen</label>
          {renderRepairmanList()}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="btn btn-primary align-self-center px-5"
        >
          {isEditing ? "Save Changes" : "Add Repair Team"}
        </button>
      </form>

      {/* Repair Team Table */}
      <h5 className="text-center mb-4">Repair Teams List</h5>
      <div className="table-responsive">
        {repairTeamList.length === 0 ? (
          <div className="alert alert-info text-center">
            No repair teams found. Add your first repair team using the form above.
          </div>
        ) : (
          <table className="table table-hover m-0">
            <thead>
              <tr>
                <th scope="col" className="text-center" style={{ width: "60px" }}>
                  №
                </th>
                <th scope="col">Name</th>
                <th scope="col">Leader</th>
                <th scope="col">Repairmen</th>
                <th
                  scope="col"
                  className="text-center"
                  style={{ width: "100px" }}
                >
                  Edit
                </th>
                <th
                  scope="col"
                  className="text-center"
                  style={{ width: "100px" }}
                >
                  Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {repairTeamList.map((team, index) => {
                const teamId = team.id || team._id;
                return (
                  <tr key={teamId}>
                    <th scope="row" className="text-center">
                      {index + 1}
                    </th>
                    <td>{team.name}</td>
                    <td>
                      {team.leader ? `${team.leader.name} ${team.leader.surname}` : 'No leader assigned'}
                    </td>
                    <td>
                      {team.repairmen && team.repairmen.length > 0 ? (
                        team.repairmen.map((rm) => (
                          <p key={rm.id || rm._id} className="m-0">
                            {rm.name} {rm.surname}
                          </p>
                        ))
                      ) : (
                        <span className="text-muted">No repairmen assigned</span>
                      )}
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-outline-warning btn-sm"
                        onClick={() => handleEdit(team)}
                      >
                        <i className="bi bi-pencil-square"></i>
                        Edit
                      </button>
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(teamId)}
                      >
                        <i className="bi bi-trash3"></i>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RepairTeamManager;
